/* Tilt the phone, the lineup turns.
 *
 * The homepage hero was a video of the five cartridges rotating. This replaces
 * the playback with the reader's own hand: tilt the screen left or right and all
 * five turn together, from the same 36-frame turntables the product pages use.
 *
 * Three inputs, one value. Whichever is available drives the same rotation:
 *   gyroscope   the point of the exercise, on a phone or tablet
 *   pointer     a laptop has no gyroscope, so mouse position across the window
 *               does the same job and the hero is not dead on a desktop
 *   drag        grab and turn it directly, on any device
 *
 * WHAT IT COSTS, because this matters on a homepage. Tilt is physically bounded,
 * so only the frames inside the range are ever shown: 11 of each set's 36 rather
 * than all of them, 2.9 MB instead of 11.1 MB. The front frame of each set loads
 * first so the lineup paints almost at once, and the video stays on screen until
 * the frames are ready, so nobody watches an empty hero.
 *
 * THINGS THAT WILL BITE, all of them real:
 *
 *  1. iOS 13 and later requires DeviceOrientationEvent.requestPermission(), and
 *     it must be called from a user gesture. There is no way to have tilt work
 *     on an iPhone without one tap. So the tap is offered as a small control,
 *     and until it is taken the pointer fallback is running instead.
 *  2. The API needs a secure context. https and localhost qualify; file:// does
 *     NOT, so a copy opened by double-click gets the pointer fallback and never
 *     the gyroscope. That is a property of the browser, not of this code.
 *  3. gamma is the left-right axis only while the device is upright. Rotate to
 *     landscape and beta becomes the left-right axis, so the axis is chosen from
 *     the current screen angle rather than assumed.
 *  4. Gyroscope output is noisy. Feeding it straight to a frame index jitters
 *     between two frames continuously, so the value is eased rather than used
 *     raw, and it is the easing that makes it feel like a weighted object.
 *  5. prefers-reduced-motion turns the whole thing off. A hero that moves when
 *     the device moves is exactly what that setting is asking us not to do.
 */
(function () {
  'use strict';

  var nbnA = window.nbnAsset || function (p) { return p; };
  var host = document.querySelector('[data-tilt]');
  if (!host) return;
  if (!document.createElement('canvas').getContext) return;
  if (matchMedia('(prefers-reduced-motion:reduce)').matches) return;

  var items;
  try { items = JSON.parse(host.getAttribute('data-tilt')); } catch (e) { return; }
  if (!items.length) return;

  /* The cartridges are links. They are four real anchors in the markup laid over
     the stage, not a click handler on the canvas, so the lineup is a product row
     with JavaScript off and over the <video> too. Paired to the frame sets BY
     INDEX, so the markup order and the data-tilt order have to agree. */
  var linkBox = host.querySelector('[data-tilt-links]');
  var links = linkBox ? Array.prototype.slice.call(linkBox.children) : [];

  var SPAN = 4.5;      // frames either side of front. 1 frame = 10deg, so +/-45
  var TILT = 32;       // degrees of physical tilt that reach the full rotation
  var PAD  = 6;        // frames loaded either side of front, one spare for blending
  var LOWPASS = 14;    // sensor smoothing, per second
  var SETTLE = 7;      // visual response, per second. Higher chases harder

  /* 9 Sep 2026, mobile: the markup can offer a small frame set per tube
     (dirPathSm + metaSm, 768px-tall frames at about a third of the bytes).
     Taken on the theme's 768px breakpoint, decided once at boot. The shared
     scale REF is h*cyl/bodyw, and h and bodyw scale together, so it is the
     same number either way and the row keeps its true relative sizes. */
  var SMALL = matchMedia('(max-width: 768px)').matches;
  var sets = items.map(function (it) {
    return {
      m: (SMALL && it.metaSm) || it.meta, dir: (SMALL && it.dirPathSm) || it.dirPath, imgs: {}, ready: false,
      spin: 0,          // this tube's own rotation, on top of the shared tilt
      full: false,      // whether all 36 frames are loaded, for a whole turn
      loading: false,
      settle: 0
    };
  });

  /* ------------------------------------------------------------------ frames */

  function load(src) {
    return new Promise(function (res) {
      var im = new Image();
      im.onload = function () { res(im); };
      im.onerror = function () { res(null); };
      im.src = src;
    });
  }
  function frameIndex(m, offset) {
    return ((Math.round(m.front + offset) % m.n) + m.n) % m.n;
  }
  function src(s, idx) {
    return nbnA(s.dir + 'f' + String(idx).padStart(2, '0') + '.webp');
  }

  // Front faces first, so the lineup can paint before the rest arrives.
  Promise.all(sets.map(function (s, i) {
    var idx = frameIndex(s.m, 0);
    return load(src(s, idx)).then(function (im) { if (im) s.imgs[idx] = im; });
  })).then(function () {
    if (!Object.keys(sets[0].imgs).length) return;      // leave the video alone
    begin();
    return Promise.all(sets.map(function (s) {
      var jobs = [];
      for (var d = -PAD; d <= PAD; d++) {
        if (d === 0) continue;
        (function (idx) {
          jobs.push(load(src(s, idx)).then(function (im) { if (im) s.imgs[idx] = im; }));
        })(frameIndex(s.m, d));
      }
      return Promise.all(jobs).then(function () { s.ready = true; });
    }));
  });

  /* ------------------------------------------------------------------ layout */

  var canvas, ctx, W = 0, H = 0, dpr = 1;
  // The tallest tube per unit of scale, so the row fits without cropping and the
  // five stay at true relative size. Same reference the scroll stage uses.
  var REF = Math.max.apply(null, sets.map(function (s) {
    return s.m.h * s.m.cyl / s.m.bodyw;
  }));

  function measure() {
    var r = host.getBoundingClientRect();
    W = Math.round(r.width); H = Math.round(r.height);
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    fitLinks();
  }

  /* Put each link on the cartridge it covers, from the same three numbers paint()
     draws with: one display scale for the whole row, the slot centre, and the
     standing line. Measured rather than guessed, so the target is the cartridge
     and not the empty air beside it, at every viewport width and after a resize.

     Two corrections on top of the drawn box. A cartridge that draws narrower than
     a fingertip is widened to 44px, because a target you can hit outranks a target
     that is exact. And nothing is ever wider than its own slot, or neighbours
     would overlap and the wrong product would open. */
  var TOUCH_MIN = 44;
  function fitLinks() {
    if (!links.length || !W || !H) return;
    var n = sets.length, slot = W / n, disp = (H - 12) / REF;
    for (var i = 0; i < n && i < links.length; i++) {
      var m = sets[i].m;
      var scale = (m.cyl * disp) / m.bodyw;
      var drawnLeft = slot * i + slot / 2 - m.cx * scale;
      var mid = drawnLeft + (m.w * scale) / 2;
      var w = Math.min(Math.max(m.w * scale, TOUCH_MIN), slot);
      var top = (H - 6) - m.base * scale;
      var st = links[i].style;
      st.left   = Math.round(mid - w / 2) + 'px';
      st.top    = Math.round(top) + 'px';
      st.width  = Math.round(w) + 'px';
      st.height = Math.round(Math.min(m.h * scale, H - top)) + 'px';
    }
    linkBox.classList.add('is-fitted');
  }

  /* One frame at a time, no blending.

     Cross-dissolving the two frames either side of the true angle did make the
     motion continuous, but it does it by drawing two photographs of the same
     cartridge, 10 degrees apart, on top of each other. On a rotating label that
     reads as a doubled, smeared wordmark rather than as motion blur. Crisp and
     stepped beats smooth and distorted on a product shot, so the nearest frame
     is drawn and nothing else. The easing below is what carries the feel. */
  function paint(offset) {
    if (!W || !H) return;
    ctx.clearRect(0, 0, W, H);
    var n = sets.length;
    var slot = W / n;
    // One scale for all five, so a 300 mL cartridge is the same size as another.
    var disp = (H - 12) / REF;
    for (var i = 0; i < n; i++) {
      var s = sets[i], m = s.m;
      var scale = (m.cyl * disp) / m.bodyw;
      var x = slot * i + slot / 2;
      var top = (H - 6) - m.base * scale;                  // one standing line
      // Global tilt, plus whatever this one tube has been spun to on its own.
      var im = s.imgs[frameIndex(m, offset + s.spin)];
      if (!im) im = s.imgs[frameIndex(m, 0)];
      if (!im) continue;
      ctx.drawImage(im, x - m.cx * scale, top, m.w * scale, m.h * scale);
    }
  }

  // Which tube is under this x. The row is five equal slots.
  function tubeAt(clientX) {
    var r = canvas.getBoundingClientRect();
    var i = Math.floor(((clientX - r.left) / r.width) * sets.length);
    return Math.max(0, Math.min(sets.length - 1, i));
  }

  /* ------------------------------------------------------------------- drive */

  var target = 0, eased = 0, shown = 0, running = false, last = 0;

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function setFromAngle(deg) { target = clamp(deg / TILT, -1, 1) * SPAN; }

  /* Two cascaded filters, both driven by elapsed time rather than by counting
     events or frames. That matters twice over:

       A fixed fraction per FRAME settles twice as fast on a 120Hz phone as on a
       60Hz one, so the feel changed with the handset.

       A fixed fraction per SENSOR EVENT ties the smoothing to how often the
       hardware happens to report. A phone firing at 60Hz converged in a tenth of
       a second while a slower source crawled, and the same filter that felt right
       on one device felt broken on another.

     Stage one takes the jitter out of the sensor, which is noisy by a degree or
     two even lying still on a desk. Stage two is the weight: it is what makes the
     lineup feel like an object with mass rather than a value being assigned. One
     aggressive stage cannot do both, it just adds lag. */
  function loop(now) {
    var dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016;
    last = now;
    eased += (target - eased) * (1 - Math.exp(-LOWPASS * dt));
    shown += (eased  - shown) * (1 - Math.exp(-SETTLE  * dt));
    if (Math.abs(target - shown) < 0.0012) { eased = target; shown = target; }
    settleSpins(now, dt);
    paint(shown);
    if (running) requestAnimationFrame(loop);
  }

  /* Pointer, the desktop fallback. Position across the window rather than a drag,
     so the hero responds before anyone thinks to touch it. */
  var dragging = false;

  /* A drag that ends on an anchor still fires a click, so turning a cartridge
     would navigate away mid-gesture. The click is cancelled in the capture phase
     instead, before the anchor ever sees it. The timer is the safety net: a
     gesture that ends without a click (a pointercancel, a drag off the stage)
     must not leave the block armed for whatever is clicked next, including a
     keyboard Enter, which fires a click with no pointer sequence at all. */
  var suppressClick = false, blockTimer = 0;
  var TAP_SLOP = 8;      // px of travel still counted as a tap, not a drag

  function armClickBlock() {
    suppressClick = true;
    clearTimeout(blockTimer);
    blockTimer = setTimeout(function () { suppressClick = false; }, 400);
  }

  function bindPointer() {
    addEventListener('mousemove', function (e) {
      // Not while a cartridge is being turned. Otherwise the same pointer
      // movement spins the tube under the thumb and swings the other four with
      // it, which reads as the whole row lurching.
      if (dragging) return;
      setFromAngle(((e.clientX / innerWidth) * 2 - 1) * TILT);
    }, { passive: true });
  }

  /* Grab one cartridge and turn it all the way round.

     The tilt moves all five together; this turns a single one on its own, a full
     360, the way the product pages do. Both apply at once, so a tube you have
     spun still responds to tilting.

     WHY THE FRAMES ARE NOT ALREADY LOADED. The hero loads only the 11 frames
     inside the reachable tilt range, which is what keeps it at 2.9 MB instead of
     11.1 MB. A whole turn needs all 36. So the rest of THAT ONE SET is fetched
     the moment you take hold of it, roughly 400 KB, and until it arrives the
     rotation is held to the frames that exist rather than falling back to a
     wrong one. Nobody pays for the other four sets unless they grab those too. */
  function loadFull(s) {
    if (s.full || s.loading) return;
    s.loading = true;
    var jobs = [];
    for (var i = 0; i < s.m.n; i++) {
      if (s.imgs[i]) continue;
      (function (idx) {
        jobs.push(load(src(s, idx)).then(function (im) { if (im) s.imgs[idx] = im; }));
      })(i);
    }
    Promise.all(jobs).then(function () { s.full = true; s.loading = false; });
  }

  /* Bound to the STAGE rather than to the canvas. The anchors sit on top of the
     canvas, so the canvas no longer receives the pointer at all; the stage is
     the one element under every part of the gesture, and pointer events from
     the anchors and from the canvas both reach it by bubbling. */
  function bindDrag() {
    var id = null, x0 = 0, y0 = 0, base = 0, held = -1, moved = false;

    host.addEventListener('pointerdown', function (e) {
      if (e.button > 0) return;              // leave the right-click menu alone
      id = e.pointerId; x0 = e.clientX; y0 = e.clientY; moved = false;
      held = tubeAt(e.clientX);
      base = sets[held].spin;
      sets[held].settle = 0;
      loadFull(sets[held]);
      dragging = true;
      /* Capture on the element the gesture actually started on, NOT on the
         stage. With capture set, Chrome dispatches the click to the capturing
         element, so capturing the stage would swallow every tap: the anchor
         under the finger would never see its own click and no cartridge would
         open. Capturing the target keeps the click where it belongs and still
         delivers pointermove and pointerup after the pointer leaves the stage,
         which is the whole reason capture is here. */
      try { (e.target && e.target.setPointerCapture ? e.target : host).setPointerCapture(id); } catch (err) {}
      host.classList.add('is-grabbing');
    });

    host.addEventListener('pointermove', function (e) {
      if (e.pointerId !== id || held < 0) return;
      /* Travel has to clear a threshold before this counts as a drag. Taking any
         movement at all, which is what this did before the cartridges were links,
         means a mouse click that slides a pixel between press and release reads
         as a drag and the link never opens. Below the threshold the cartridge is
         not turned either, so a tap leaves it facing front. */
      if (!moved && (Math.abs(e.clientX - x0) > TAP_SLOP || Math.abs(e.clientY - y0) > TAP_SLOP)) moved = true;
      if (!moved) return;
      e.preventDefault();        // only once this is a turn, never during a tap
      var s = sets[held];
      var want = base - (e.clientX - x0) / 16;
      // Until the whole set is in, stay inside the frames that exist, so a fast
      // drag cannot land on a frame that has not arrived.
      s.spin = s.full ? want : clamp(want, -PAD + SPAN, PAD - SPAN);
    });

    ['pointerup', 'pointercancel'].forEach(function (t) {
      host.addEventListener(t, function (e) {
        if (e.pointerId !== id) return;
        if (held >= 0 && moved) { sets[held].settle = performance.now() + 2200; armClickBlock(); }
        id = null; held = -1; dragging = false;
        host.classList.remove('is-grabbing');
      });
    });

    // Capture phase: the anchor must not act on a click that ended a drag.
    host.addEventListener('click', function (e) {
      if (!suppressClick) return;
      suppressClick = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    // A link is draggable by default, which on a mouse starts a link drag and
    // kills the turn halfway through.
    host.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  /* A tube left spun stays put while you are playing with it, then eases back to
     face front. A hero that keeps five cartridges at five random angles stops
     presenting the product, which is the one job it has. */
  function settleSpins(now, dt) {
    for (var i = 0; i < sets.length; i++) {
      var s = sets[i];
      if (!s.spin || !s.settle || now < s.settle) continue;
      var n = s.m.n, v = s.spin % n;
      if (v > n / 2) v -= n;
      if (v < -n / 2) v += n;
      s.spin = v * Math.exp(-7 * dt);
      if (Math.abs(s.spin) < 0.03) { s.spin = 0; s.settle = 0; }
    }
  }

  /* Gyroscope. The axis depends on how the device is being held: gamma is
     left-to-right upright, beta is left-to-right in landscape, and the sign
     flips depending which way it was turned. */
  function onOrientation(e) {
    if (e.gamma === null && e.beta === null) return;
    var a = (screen.orientation && screen.orientation.angle) || window.orientation || 0;
    var deg;
    if (a === 90)       deg = -(e.beta || 0);
    else if (a === -90 || a === 270) deg = (e.beta || 0);
    else if (a === 180) deg = -(e.gamma || 0);
    else                deg = (e.gamma || 0);
    setFromAngle(deg);
    host.classList.add('is-tilting');
  }

  function bindTilt() {
    if (!('DeviceOrientationEvent' in window)) return;
    var needsAsk = typeof DeviceOrientationEvent.requestPermission === 'function';
    if (!needsAsk) {
      addEventListener('deviceorientation', onOrientation, { passive: true });
      return;
    }
    /* iOS, and this is NOT a demo-only step. Since iOS 13 Apple requires
       requestPermission() to be called from a user gesture on every site,
       production included. No site can skip it. Android needs nothing.

       So rather than a button bolted under the hero, the lineup itself becomes
       the target and the hint line becomes its label: "Tap to tilt". One tap on
       the cartridges asks, which is also where a reader's thumb already is. The
       real <button> stays in the markup for keyboard and screen reader users,
       styled as the same quiet line rather than as chrome. */
    var btn = host.parentElement.querySelector('[data-tilt-ask]');
    var hint = host.parentElement.querySelector('.tilt-hint');
    host.classList.add('needs-permission');
    /* No button chrome. The lineup is the target and the hint is the whole of
       the invitation, because "Enable tilt" is the language of a settings screen
       and nobody taps a permission control for fun. "Tap to start" is an offer.
       The real <button> stays in the DOM, visually hidden, so the feature is
       still reachable by keyboard and announced to a screen reader. */
    if (hint) hint.textContent = 'Tap beside a cartridge to turn them. Tap one to open it';
    if (btn) { btn.hidden = false; btn.textContent = 'Start the tilt effect'; }

    function ask() {
      DeviceOrientationEvent.requestPermission().then(function (state) {
        if (state === 'granted') {
          addEventListener('deviceorientation', onOrientation, { passive: true });
          host.classList.remove('needs-permission');
          if (btn) btn.hidden = true;
          if (hint) hint.textContent = 'Tilt your screen to turn them. Tap one to open it';
          host.classList.add('just-started');
        } else {
          // Declined is permanent for the session, so say so once and stop
          // offering. The pointer and the drag still work.
          if (hint) hint.textContent = 'Motion access declined. Drag to turn them, tap to open';
          if (btn) btn.hidden = true;
          host.classList.remove('needs-permission');
        }
      }).catch(function () {
        if (hint) hint.textContent = 'Drag to turn them, tap one to open it';
        if (btn) btn.hidden = true;
        host.classList.remove('needs-permission');
      });
    }
    if (btn) btn.addEventListener('click', ask);
    // A tap on the lineup counts as the gesture too. Guarded so a drag does not
    // fire it: only a tap that did not travel.
    /* The cartridges are links now, so this tap and a tap that opens a product
       are the same gesture. iOS gets the permission prompt first and that one tap
       does not navigate: the dialog it raises is its own answer to the reader, so
       nothing looks ignored, and every tap after it opens the product. Only while
       the gesture is still owed, which on every other platform is never. */
    var COARSE = matchMedia('(hover: none) and (pointer: coarse)');
    var dx0 = 0, dy0 = 0, onCartridge = false;
    host.addEventListener('pointerdown', function (e) {
      dx0 = e.clientX; dy0 = e.clientY;
      onCartridge = !!(e.target && e.target.closest && e.target.closest('[data-tilt-links] a'));
    });
    host.addEventListener('pointerup', function (e) {
      if (!host.classList.contains('needs-permission')) return;
      /* COARSE POINTERS ONLY. Chrome on a Mac reports requestPermission as a
         function and then never fires an orientation event, so this branch is
         taken on desktops where tilt cannot work at all; the stylesheet already
         hides the invitation there for exactly that reason (9 Sep 2026). Left
         unguarded it would go on claiming the first click of the session on a
         machine with no accelerometer, and that click is now a product link.
         Measured on the live page before this guard: the first click on a
         cartridge opened nothing. Same media query as the stylesheet, so the
         gesture is only ever taken where the invitation is on screen. */
      if (!COARSE.matches) return;
      /* NOT ON A CARTRIDGE. A tap on a cartridge opens it, first tap, every
         device. The permission gesture takes the rest of the stage, which is
         most of it, and the button below. Selling the product outranks the
         effect. */
      if (onCartridge) return;
      if (Math.abs(e.clientX - dx0) > TAP_SLOP || Math.abs(e.clientY - dy0) > TAP_SLOP) return;
      armClickBlock();
      ask();
    });
  }

  function begin() {
    canvas = document.createElement('canvas');
    canvas.className = 'tilt-canvas';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label',
      'The four Octogun and Newborn cartridges. Tilt the screen, move the pointer or drag to turn them.');
    ctx = canvas.getContext('2d');
    host.appendChild(canvas);
    host.classList.add('is-live');
    /* The film under the canvas is hidden from here on, so stop decoding it.
       It stays in the DOM: on a phone it is what gives the stage its height. */
    var film = host.querySelector('video');
    if (film && !film.paused) { try { film.pause(); } catch (e) {} }

    measure();
    running = true;
    requestAnimationFrame(loop);

    addEventListener('resize', function () { measure(); paint(shown); });
    if (window.ResizeObserver) new ResizeObserver(function () { measure(); paint(shown); }).observe(host);
    document.addEventListener('visibilitychange', function () {
      running = !document.hidden;
      if (running) requestAnimationFrame(loop);
    });

    bindPointer();
    bindDrag();
    bindTilt();
  }
}());
